import React, { useEffect, useRef, useState } from 'react';
import { FaPaperPlane, FaPaperclip, FaStop } from 'react-icons/fa';
import { LuAudioWaveform } from 'react-icons/lu';
import {
  PendingAttachment,
  TeamChatAgent,
} from '../../hooks/useTeamChatSocket';
import type { CardRef } from '../../client';
import {
  UsableCardRef,
  cardRefTokenAt,
  filterCardRefs,
  refTitleExcerpt,
} from '../../lib/card-refs';
import ChatAttachmentPreview from './ChatAttachmentPreview';

const ACCEPTED_FILE_TYPES =
  'image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

interface ChatComposerProps {
  onSend: (text: string) => boolean;
  disabled: boolean;
  isProcessing: boolean;
  pendingAttachments: PendingAttachment[];
  onAttach: (file: File) => void;
  onRemoveAttachment: (clientId: string) => void;
  /** Stop the in-flight response (shown while processing) */
  onStop: () => void;
  /** Team agents offered by the @mention autocomplete */
  agents: TeamChatAgent[];
  /**
   * Called when the user selects an agent marked `unavailable`. `complete`
   * inserts the mention at the spot the user was typing — call it once the
   * agent has been made available (e.g. added to the project).
   */
  onUnavailableMention?: (agent: TeamChatAgent, complete: () => void) => void;
  /** Idle-state placeholder (defaults to the team-chat wording) */
  placeholder?: string;
  /**
   * Project cards offered by the #reference autocomplete (JIRA-style
   * #AFB-1). Only the bare reference is inserted; rendering expands it.
   */
  cardRefs?: CardRef[];
  /**
   * One-shot programmatic draft (e.g. "@Alice " from a member's Message
   * button). Applied and focused whenever `nonce` changes.
   */
  seed?: { text: string; nonce: number } | null;
  /**
   * Reports the nonce this composer just applied. The owner records it above
   * the remount boundary, so a seed is never re-applied to a fresh composer
   * instance over a draft the person has since edited.
   */
  onSeedApplied?: (nonce: number) => void;
  /** Focus request that survives composer subtree remounts. */
  focusNonce?: number | null;
  /** Controlled draft used by team chat while its inline voice deck is open. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** Presence enables the team-chat-only voice action. */
  onVoiceStart?: () => void;
  /**
   * Composer commands offered when the message starts with "/" (e.g.
   * /compact). The surface intercepts them in onSend; the composer only
   * autocompletes.
   */
  slashCommands?: SlashCommand[];
}

export interface SlashCommand {
  /** Including the leading slash, e.g. "/compact" */
  command: string;
  description: string;
}

/** Commands only fire as the whole message: a single /token, nothing else */
function slashTokenOf(text: string): string | null {
  return /^\/[a-z]*$/i.test(text.trim()) ? text.trim().toLowerCase() : null;
}

/** The @token under the cursor, if the caret is inside one */
function mentionTokenAt(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const atIndex = text.lastIndexOf('@', caret - 1);
  if (atIndex === -1) return null;
  // The @ must start a word (line start or after whitespace)
  if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) return null;
  const query = text.slice(atIndex + 1, caret);
  // Mentions can contain spaces, but two consecutive spaces or a newline
  // means the user moved on
  if (/\n|\s{2,}/.test(query)) return null;
  return { start: atIndex, query };
}

/**
 * Message input. Enter sends, Shift+Enter inserts a newline. Typing
 * @ suggests team agents; Tab (or Enter/click) completes the mention.
 * Stays enabled while a response is processing — messages sent then are
 * queued server-side.
 */
const ChatComposer: React.FC<ChatComposerProps> = ({
  onSend,
  disabled,
  isProcessing,
  pendingAttachments,
  onAttach,
  onRemoveAttachment,
  onStop,
  agents,
  cardRefs,
  onUnavailableMention,
  placeholder,
  seed,
  onSeedApplied,
  focusNonce,
  value,
  onValueChange,
  onVoiceStart,
  slashCommands,
}) => {
  const [internalText, setInternalText] = useState('');
  const [attachError, setAttachError] = useState<string | null>(null);
  const [mentionOptions, setMentionOptions] = useState<TeamChatAgent[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [refOptions, setRefOptions] = useState<UsableCardRef[]>([]);
  const [refIndex, setRefIndex] = useState(0);
  const [slashOptions, setSlashOptions] = useState<SlashCommand[]>([]);
  const [slashIndex, setSlashIndex] = useState(0);
  const mentionStartRef = useRef<number>(0);
  const refStartRef = useRef<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const text = value ?? internalText;

  const setText = (next: string): void => {
    if (value === undefined) setInternalText(next);
    onValueChange?.(next);
  };

  const uploading = pendingAttachments.some(
    (attachment) => attachment.status === 'uploading',
  );

  // Apply a programmatic draft exactly once per nonce, replacing whatever
  // is in the composer (callers only seed on explicit user intent)
  const appliedSeedNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!seed || appliedSeedNonce.current === seed.nonce) return;
    appliedSeedNonce.current = seed.nonce;
    onSeedApplied?.(seed.nonce);
    setText(seed.text);
    setMentionOptions([]);
    setRefOptions([]);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.value = seed.text;
      textarea.focus();
      textarea.setSelectionRange(seed.text.length, seed.text.length);
    }
  }, [seed]);

  // Unlike draft seeds, focus is safe to repeat when the composer subtree
  // changes (for example when voice support loads after the chat opens), or
  // when the chat socket finishes connecting. A disabled textarea cannot take
  // focus, so retain the request until the composer becomes interactive.
  useEffect(() => {
    if (focusNonce == null || disabled) return;
    textareaRef.current?.focus();
  }, [focusNonce, disabled]);

  const handleFiles = (files: FileList | null) => {
    setAttachError(null);
    if (!files) return;

    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(`"${file.name}" is larger than 10 MB.`);
        continue;
      }
      onAttach(file);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const refreshSlashOptions = (value: string) => {
    const token = slashCommands?.length ? slashTokenOf(value) : null;
    if (!token) {
      setSlashOptions([]);
      return;
    }
    setSlashOptions(
      slashCommands!.filter((entry) => entry.command.startsWith(token)),
    );
    setSlashIndex(0);
  };

  const completeSlashCommand = (entry: SlashCommand) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    setText(entry.command);
    textarea.value = entry.command;
    textarea.focus();
    textarea.setSelectionRange(entry.command.length, entry.command.length);
    setSlashOptions([]);
  };

  const refreshMentionOptions = (value: string, caret: number) => {
    const token = mentionTokenAt(value, caret);
    if (!token) {
      setMentionOptions([]);
    } else {
      const query = token.query.toLowerCase();
      const matches = agents.filter((agent) =>
        agent.name.toLowerCase().startsWith(query),
      );
      mentionStartRef.current = token.start;
      setMentionOptions(matches);
      setMentionIndex(0);
    }

    if (!cardRefs || cardRefs.length === 0) return;
    const refToken = cardRefTokenAt(value, caret);
    if (!refToken) {
      setRefOptions([]);
      return;
    }
    refStartRef.current = refToken.start;
    setRefOptions(filterCardRefs(cardRefs, refToken.query));
    setRefIndex(0);
  };

  const completeCardRef = (ref: UsableCardRef) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const caret = textarea.selectionStart ?? textarea.value.length;
    const before = textarea.value.slice(0, refStartRef.current);
    const after = textarea.value.slice(caret);
    // Only the bare reference is stored; readers see it expanded.
    const completed = `${before}${ref.reference} ${after}`;
    setText(completed);
    textarea.value = completed;
    const position = before.length + ref.reference.length + 1;
    textarea.focus();
    textarea.setSelectionRange(position, position);
    setRefOptions([]);
  };

  /** Insert `@name ` over the token between `start` and `caret`. */
  const insertMentionAt = (
    agent: TeamChatAgent,
    start: number,
    caret: number,
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const before = textarea.value.slice(0, start);
    const after = textarea.value.slice(caret);
    const completed = `${before}@${agent.name} ${after}`;
    setText(completed);
    textarea.value = completed;
    const position = before.length + agent.name.length + 2;
    textarea.focus();
    textarea.setSelectionRange(position, position);
    setMentionOptions([]);
  };

  const completeMention = (agent: TeamChatAgent) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const caret = textarea.selectionStart ?? textarea.value.length;
    const start = mentionStartRef.current;
    if (agent.unavailable && onUnavailableMention) {
      // Capture the token position now: by the time the caller finishes
      // (e.g. a confirmation modal), focus and selection may have moved.
      setMentionOptions([]);
      onUnavailableMention(agent, () => insertMentionAt(agent, start, caret));
      return;
    }
    insertMentionAt(agent, start, caret);
  };

  const send = () => {
    // Read the live DOM value, not just state: programmatic input (tests,
    // extensions, IMEs) can set the value without React state having
    // committed yet, and a send must carry what the user actually sees
    const trimmed = (textareaRef.current?.value ?? text).trim();
    console.log(`[chat] composer send: ${trimmed.length} chars`);
    if (!trimmed) return;
    if (onSend(trimmed)) {
      setText('');
      setMentionOptions([]);
      setSlashOptions([]);
      if (textareaRef.current) {
        textareaRef.current.value = '';
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (slashOptions.length > 0) {
      const typed = (textareaRef.current?.value ?? text).trim().toLowerCase();
      if (event.key === 'Tab') {
        event.preventDefault();
        completeSlashCommand(slashOptions[slashIndex]);
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        // A fully typed command sends; a prefix completes first
        if (typed !== slashOptions[slashIndex].command) {
          event.preventDefault();
          completeSlashCommand(slashOptions[slashIndex]);
          return;
        }
        setSlashOptions([]);
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSlashIndex((index) => (index + 1) % slashOptions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSlashIndex(
          (index) => (index - 1 + slashOptions.length) % slashOptions.length,
        );
        return;
      }
      if (event.key === 'Escape') {
        setSlashOptions([]);
        return;
      }
    }

    if (refOptions.length > 0) {
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault();
        completeCardRef(refOptions[refIndex]);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setRefIndex((index) => (index + 1) % refOptions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setRefIndex(
          (index) => (index - 1 + refOptions.length) % refOptions.length,
        );
        return;
      }
      if (event.key === 'Escape') {
        setRefOptions([]);
        return;
      }
    }

    if (mentionOptions.length > 0) {
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault();
        completeMention(mentionOptions[mentionIndex]);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionIndex((index) => (index + 1) % mentionOptions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionIndex(
          (index) =>
            (index - 1 + mentionOptions.length) % mentionOptions.length,
        );
        return;
      }
      if (event.key === 'Escape') {
        setMentionOptions([]);
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
    refreshSlashOptions(event.target.value);
    refreshMentionOptions(
      event.target.value,
      event.target.selectionStart ?? event.target.value.length,
    );
    const textarea = event.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  };

  return (
    <div className="chat-composer-container">
      {attachError && (
        <div className="chat-attach-error" onClick={() => setAttachError(null)}>
          {attachError}
        </div>
      )}
      <ChatAttachmentPreview
        attachments={pendingAttachments}
        onRemove={onRemoveAttachment}
      />
      <div className="chat-composer">
        {slashOptions.length > 0 && (
          <div className="chat-mention-popup" data-testid="chat-slash-popup">
            {slashOptions.map((entry, index) => (
              <button
                key={entry.command}
                className={`chat-mention-option ${
                  index === slashIndex ? 'chat-mention-option-active' : ''
                }`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  completeSlashCommand(entry);
                }}
                data-testid={`chat-slash-option-${entry.command.slice(1)}`}>
                {entry.command} — {entry.description}
              </button>
            ))}
          </div>
        )}
        {mentionOptions.length > 0 && (
          <div className="chat-mention-popup" data-testid="chat-mention-popup">
            {mentionOptions.map((agent, index) => (
              <button
                key={agent.id}
                className={`chat-mention-option ${
                  index === mentionIndex ? 'chat-mention-option-active' : ''
                }${agent.unavailable ? ' chat-mention-option-unavailable' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  completeMention(agent);
                }}
                title={
                  agent.unavailable ? 'Not on this project yet' : undefined
                }
                data-testid={`chat-mention-option-${agent.id}`}>
                @{agent.name}
              </button>
            ))}
          </div>
        )}
        {refOptions.length > 0 && (
          <div className="chat-mention-popup" data-testid="chat-cardref-popup">
            {refOptions.map((ref, index) => (
              <button
                key={ref.cardId}
                className={`chat-mention-option ${
                  index === refIndex ? 'chat-mention-option-active' : ''
                }`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  completeCardRef(ref);
                }}
                data-testid={`chat-cardref-option-${ref.cardId}`}>
                {ref.reference} — {refTitleExcerpt(ref.title)}
              </button>
            ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple
          style={{ display: 'none' }}
          disabled={disabled}
          onChange={(event) => handleFiles(event.target.files)}
        />
        <button
          className="chat-composer-attach"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          title="Attach images or PDFs">
          <FaPaperclip />
        </button>
        <textarea
          ref={textareaRef}
          className="chat-composer-input"
          title="Use @ to address agents and humans"
          placeholder={
            isProcessing
              ? 'Type a message — the agents will see it right away…'
              : placeholder || 'Type here to chat'
          }
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
        />
        {isProcessing && (
          <button
            className="chat-composer-stop"
            onClick={onStop}
            disabled={disabled}
            data-testid="chat-stop-button"
            title="Stop the current response">
            <FaStop />
          </button>
        )}
        <button
          className="chat-composer-send"
          onClick={send}
          disabled={disabled || uploading || text.trim() === ''}
          aria-label="Send message"
          title={uploading ? 'Waiting for uploads…' : 'Send (Enter)'}>
          <FaPaperPlane />
        </button>
        {onVoiceStart && (
          <button
            className="chat-composer-voice"
            onClick={onVoiceStart}
            disabled={disabled}
            aria-label="Start voice chat"
            title="Start voice chat">
            <LuAudioWaveform />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatComposer;
